/**
 * ABOUTME: Card shown in chat when the user has run out of credits.
 * Displays current balance, the credits required for the blocked operation,
 * and an upgrade button linking to /billing/plans.
 */
import { CreditCard, AlertTriangle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { CHAT_CARD } from '@/components/chat/chatCard'
import { canShowStorePurchases } from '@/platform'

interface InsufficientCreditsCardProps {
  creditsRemaining: number
  creditsRequired?: number
  operation?: string
}

function upgradeHint(goal: string): string {
  return canShowStorePurchases()
    ? `Upgrade your plan to ${goal}.`
    : `Plans are managed on the web; add credits there to ${goal}.`
}

// Map snake_case operation identifiers to human-friendly verbs
const OPERATION_LABELS: Record<string, string> = {
  agent_chat: 'This message',
  chat: 'This message',
  text_generation: 'Text generation',
  image_generation: 'Image generation',
  generate_smb_photo: 'Image generation',
  generate_image_variation: 'Image generation',
  create_image_asset: 'Image generation',
  generate_image_from_prompt: 'Image generation',
  generate_image: 'Image generation',
  video_15s: 'Video generation',
  video_30s: 'Video generation',
  create_video_asset: 'Video generation',
  render_video: 'Video rendering',
  generate_music: 'Music generation',
  create_reel_from_template: 'Reel generation',
  preview_reel_frame: 'Reel preview',
  sms_send: 'SMS send',
  email_send: 'Email send',
}

function formatOperation(operation?: string): string {
  if (!operation) return 'This action'
  return (
    OPERATION_LABELS[operation] ||
    operation.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

export function InsufficientCreditsCard({
  creditsRemaining,
  creditsRequired,
  operation,
}: InsufficientCreditsCardProps) {
  const navigate = useNavigate()
  const operationLabel = formatOperation(operation)
  const hasRequired = typeof creditsRequired === 'number' && creditsRequired > 0

  return (
    <div data-testid="insufficient-credits-card" className={cn(CHAT_CARD, 'my-2 space-y-2')}>
      <div className="flex items-center gap-2">
        <AlertTriangle className="text-destructive size-4 shrink-0" />
        <span className="text-destructive flex-1 text-sm font-medium">
          Out of credits{operation ? ` — ${operationLabel.toLowerCase()} paused` : ''}
        </span>
      </div>
      <div className="space-y-3">
        {hasRequired ? (
          <p className="text-muted-foreground text-xs">
            <span className="text-foreground font-semibold">{operationLabel}</span> needs{' '}
            <span className="text-foreground font-semibold">{creditsRequired}</span> credit
            {creditsRequired !== 1 ? 's' : ''}, you have{' '}
            <span className="text-foreground font-semibold">{creditsRemaining}</span>.{' '}
            {upgradeHint('keep creating')}
          </p>
        ) : (
          <p className="text-muted-foreground text-xs">
            Your current balance is{' '}
            <span className="text-foreground font-semibold">{creditsRemaining}</span> credit
            {creditsRemaining !== 1 ? 's' : ''}.{' '}
            {upgradeHint('continue generating content, images, and videos')}
          </p>
        )}

        {canShowStorePurchases() && (
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => navigate('/settings/plan#plan')}
              className="gap-1.5 text-xs"
              data-testid="upgrade-plan-button"
            >
              <CreditCard className="size-3.5" />
              Upgrade plan
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/settings/plan#credits')}
              className="text-xs"
              data-testid="buy-credits-button"
            >
              Buy credits
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
